import { resolveCompanyPlatformHeroMotion } from './companyPlatformHeroMotion.js';

/** One live portal expands in its own section, after the objections story. */
class VpCompanyPlatformHeroElement extends HTMLElement {
  static observedAttributes = ['data-motion'];
  private frame = 0;
  attributeChangedCallback() { if (this.isConnected) this.measure(); }
  private observer?: ResizeObserver;
  private reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  private scrollHost: HTMLElement | Window = window;
  private geometry?: { x: number; y: number; scale: number; endX: number; endY: number; range: number };

  connectedCallback() {
    this.scrollHost = window;
    this.addEventListener('open-matching-demo', this.openDemo);
    window.addEventListener('scroll', this.schedule, { passive: true, capture: true });
    window.addEventListener('resize', this.measure, { passive: true });
    this.reducedMotion.addEventListener('change', this.measure);
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      for (let parent = this.parentElement; parent; parent = parent.parentElement) {
        if (/(auto|scroll)/u.test(getComputedStyle(parent).overflowY)) {
          this.scrollHost = parent;
          break;
        }
      }
      this.observer = new ResizeObserver(this.measure);
      for (const node of this.querySelectorAll(
        '.vp-company-platform-sticky, .vp-company-platform-copy, .vp-company-platform-preview-slot, .vp-company-platform-demo',
      ))
        this.observer.observe(node);
      this.measure();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('open-matching-demo', this.openDemo);
    window.removeEventListener('scroll', this.schedule, { capture: true });
    window.removeEventListener('resize', this.measure);
    this.reducedMotion.removeEventListener('change', this.measure);
    this.observer?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  private measure = () => {
    let animated = this.dataset.motion !== 'off' && !this.reducedMotion.matches && !this.closest('.vp-editor-preview-page') && window.innerHeight >= 740 && window.innerWidth > 1024;
    this.classList.toggle('is-animated', animated);
    this.classList.toggle('is-static', !animated);
    const demo = this.querySelector<HTMLElement>('.vp-company-platform-demo');
    const stage = this.querySelector<HTMLElement>('.vp-company-platform-sticky');
    const slot = this.querySelector<HTMLElement>('.vp-company-platform-preview-slot');
    if (!demo || !stage || !slot) return;
    const copy = this.querySelector<HTMLElement>('.vp-company-platform-copy');
    // Long editorial headings must remain readable instead of clipping in a pinned viewport.
    if (animated && copy && copy.offsetHeight > window.innerHeight - 120) {
      animated = false;
      this.classList.remove('is-animated');
      this.classList.add('is-static');
    }
    if (!animated) {
      this.geometry = undefined;
      demo.style.removeProperty('transform');
      this.style.setProperty('--vp-company-copy-exit', '0');
      this.setInteractive(true);
      return;
    }
    const stageRect = stage.getBoundingClientRect();
    const slotRect = slot.getBoundingClientRect();
    const scale = Math.min(slotRect.width / demo.offsetWidth, slotRect.height / demo.offsetHeight, 1);
    this.geometry = {
      scale,
      x: slotRect.left - stageRect.left + (slotRect.width - demo.offsetWidth * scale) / 2,
      y: slotRect.top - stageRect.top + (slotRect.height - demo.offsetHeight * scale) / 2,
      endX: (stage.clientWidth - demo.offsetWidth) / 2,
      endY: Math.max(0, (stage.clientHeight - demo.offsetHeight) / 2),
      range: Math.max(1, this.offsetHeight - stage.offsetHeight),
    };
    this.schedule();
  };

  private setInteractive(active: boolean) {
    this.classList.toggle('is-demo-active', active);
    const portal = this.querySelector<HTMLElement>('.vp-company-platform-window');
    if (portal) portal.inert = !active;
    const copy = this.querySelector<HTMLElement>('.vp-company-platform-copy');
    if (copy) copy.inert = Boolean(this.geometry && active);
  }

  private schedule = () => {
    if (this.frame || !this.geometry) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      const geometry = this.geometry;
      const demo = this.querySelector<HTMLElement>('.vp-company-platform-demo');
      if (!geometry || !demo) return;
      const hostTop = this.scrollHost instanceof HTMLElement ? this.scrollHost.getBoundingClientRect().top : 0;
      const motion = resolveCompanyPlatformHeroMotion((hostTop - this.getBoundingClientRect().top) / geometry.range);
      const mix = (from: number, to: number) => from + (to - from) * motion.zoom;
      demo.style.transform = `translate3d(${mix(geometry.x, geometry.endX)}px, ${mix(geometry.y, geometry.endY)}px, 0) scale(${mix(geometry.scale, 1)})`;
      this.style.setProperty('--vp-company-copy-exit', String(motion.copyExit));
      this.setInteractive(motion.demoInteractive);
      const copy = this.querySelector<HTMLElement>('.vp-company-platform-copy');
      if (copy) copy.inert = motion.copyExit === 1;
    });
  };

  private openDemo = (event: Event) => {
    const demo = this.querySelector<HTMLElement>('.vp-company-platform-demo');
    const instant = (event as CustomEvent<{ instant?: boolean }>).detail?.instant;
    if (!this.geometry) {
      demo?.scrollIntoView({ block: 'start', behavior: 'auto' });
      if (instant) this.querySelector<HTMLInputElement>('.vp-company-search input')?.focus({ preventScroll: true });
      return;
    }
    const hostTop = this.scrollHost instanceof HTMLElement ? this.scrollHost.getBoundingClientRect().top : 0;
    const scrollTop = this.scrollHost instanceof HTMLElement ? this.scrollHost.scrollTop : window.scrollY;
    this.scrollHost.scrollTo({
      top: scrollTop + this.getBoundingClientRect().top - hostTop + this.geometry.range * 0.84,
      behavior: instant || this.reducedMotion.matches ? 'instant' : 'smooth',
    });
    if (instant) {
      this.setInteractive(true);
      this.querySelector<HTMLInputElement>('.vp-company-search input')?.focus({ preventScroll: true });
    }
  };
}

if (typeof customElements !== 'undefined' && !customElements.get('vp-company-platform-hero')) {
  customElements.define('vp-company-platform-hero', VpCompanyPlatformHeroElement);
}
